import React, { useState, useEffect } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { X, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };

const EMPTY = { blocked_industries: [], referred_industries: [], min_turnover: null, disable_builtin: false };

function ChipEditor({ label, color, chips, onChange, disabled, testid }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v.length >= 2 && !chips.includes(v) && chips.length < 20) { onChange([...chips, v]); setInput(''); }
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{label}</p>
      <div className="flex flex-wrap gap-1.5 items-center">
        {chips.map(k => (
          <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border group" style={{ background: `${color}10`, color, borderColor: `${color}35` }}>
            {k}
            {!disabled && <button onClick={() => onChange(chips.filter(c => c !== k))} className="opacity-50 hover:opacity-100"><X className="w-3 h-3" /></button>}
          </span>
        ))}
        {!disabled && (
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="+ keyword, Enter" data-testid={testid}
            className="text-xs px-2 py-1 rounded-sm bg-white outline-none w-32 focus:border-[#1A4C7C]"
            style={{ border: '1px dashed rgba(136,150,168,0.4)', color: C.navy }} />
        )}
        {disabled && chips.length === 0 && <span className="text-xs" style={{ color: C.slate }}>—</span>}
      </div>
    </div>
  );
}

function ClassRow({ cls, rules, onChange, canEdit }) {
  const [open, setOpen] = useState(false);
  const r = rules || EMPTY;
  const customCount = r.blocked_industries.length + r.referred_industries.length + (r.min_turnover ? 1 : 0) + (r.disable_builtin ? 1 : 0);
  const set = (k, v) => onChange({ ...r, [k]: v });

  return (
    <div className="border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`appetite-class-${cls.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F5F7FA] transition-colors" data-testid={`appetite-toggle-${cls.id}`}>
        <div>
          <span className="text-sm font-medium" style={{ color: C.navy }}>{cls.name}</span>
          <p className="text-[11px] mt-0.5" style={{ color: C.slate, textDecoration: r.disable_builtin ? 'line-through' : 'none' }}>{cls.builtin}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {customCount > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${C.blue}12`, color: C.blue }} data-testid={`appetite-custom-badge-${cls.id}`}>{customCount} custom rule{customCount > 1 ? 's' : ''}</span>}
          {open ? <ChevronUp className="w-4 h-4" style={{ color: C.slate }} /> : <ChevronDown className="w-4 h-4" style={{ color: C.slate }} />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <ChipEditor label="Blocked industries (hard out-of-appetite)" color={C.red} chips={r.blocked_industries}
            onChange={v => set('blocked_industries', v)} disabled={!canEdit} testid={`appetite-blocked-input-${cls.id}`} />
          <ChipEditor label="Referred industries (force underwriter review)" color={C.amber} chips={r.referred_industries}
            onChange={v => set('referred_industries', v)} disabled={!canEdit} testid={`appetite-referred-input-${cls.id}`} />
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Minimum annual turnover ($)</p>
              <input type="number" min="0" step="100000" value={r.min_turnover ?? ''} disabled={!canEdit}
                onChange={e => set('min_turnover', e.target.value ? Number(e.target.value) : null)}
                placeholder="No minimum" data-testid={`appetite-minturnover-${cls.id}`}
                className="text-sm px-3 py-1.5 rounded-sm bg-white outline-none w-40 focus:border-[#1A4C7C]"
                style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy, fontVariantNumeric: 'tabular-nums' }} />
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer">
              <Switch checked={r.disable_builtin} onCheckedChange={v => canEdit && set('disable_builtin', v)} disabled={!canEdit} data-testid={`appetite-disable-builtin-${cls.id}`} />
              <span className="text-xs" style={{ color: C.navy }}>Disable built-in rules for this class</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppetiteRulesPage() {
  const api = useApi();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const canEdit = ['mga_admin', 'platform_owner'].includes(user?.role);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/marais/appetite-rules').then(r => { setData(r.data); setConfig(r.data.config); }).catch(() => {});
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: cfg } = await api.put('/marais/appetite-rules', config);
      setConfig(cfg);
      setDirty(false);
      toast.success('Appetite rules saved — they apply to the next quote immediately');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (!data || !config) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 rounded-sm" />)}</div>;

  const setClass = (cid, rules) => { setConfig(c => ({ ...c, classes: { ...c.classes, [cid]: rules } })); setDirty(true); };

  return (
    <div className="space-y-5" data-testid="appetite-rules-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Appetite Rules</h2>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>No-code appetite overlay — block or refer risks per class on top of the built-in rules. Custom rules run first on every quote.</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <Switch checked={config.enabled !== false} onCheckedChange={v => { setConfig(c => ({ ...c, enabled: v })); setDirty(true); }} disabled={!canEdit} data-testid="appetite-enabled-switch" />
            <span className="text-xs font-medium" style={{ color: C.navy }}>Custom rules {config.enabled !== false ? 'active' : 'paused'}</span>
          </label>
          {canEdit && (
            <Button onClick={save} disabled={saving || !dirty} className="rounded-sm text-white" style={{ background: C.blue }} data-testid="appetite-save-btn">
              {saving ? 'Saving…' : 'Save Rules'}
            </Button>
          )}
        </div>
      </div>

      {!canEdit && (
        <Card className="p-3 rounded-sm bg-white flex items-center gap-2 text-xs" style={{ border: '1px solid rgba(232,160,32,0.35)', color: C.slate }}>
          <ShieldAlert className="w-4 h-4" style={{ color: C.amber }} /> Read-only — appetite rules can be edited by MGA admins only.
        </Card>
      )}

      <Card className="rounded-sm bg-white overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
        {data.classes.map(cls => (
          <ClassRow key={cls.id} cls={cls} rules={config.classes?.[cls.id]} onChange={r => setClass(cls.id, r)} canEdit={canEdit} />
        ))}
      </Card>
      {config.updated_by && <p className="text-[11px]" style={{ color: C.slate }}>Last saved by {config.updated_by} · {new Date(config.updated_at).toLocaleString('en-AU')}</p>}
    </div>
  );
}
